import React, { useState } from "react";
import { Helmet } from "react-helmet";

const UserGuide = () => {
  const [activeSection, setActiveSection] = useState("getting-started");
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [showFloatingButtons, setShowFloatingButtons] = useState(true);

  const sections = [
    { id: "getting-started", title: "Getting Started" },
    { id: "features", title: "Features" },
    { id: "troubleshooting", title: "Troubleshooting" },
    { id: "tips", title: "Tips & Best Practices" },
    { id: "faq", title: "FAQ" }
  ];

  const GOOGLE_FORM_ACTION_URL = 'https://docs.google.com/forms/u/0/d/e/1FAIpQLScT2_qMhmfxINfvup2ey-U7A4Y0yygZTkLcvf7s_C7Cic933g/formResponse';
  const RATING_FIELD = 'entry.2125740814';
  const COMMENT_FIELD = 'entry.1473768020';
  const EMAIL_FIELD = 'entry.1592077490';

  const handleFeedbackSubmit = (e) => {
    e.preventDefault();
    if (window.gtag) {
      window.gtag('event', 'feedback_submit', {
        event_category: 'engagement',
        event_label: 'Feedback Submitted'
      });
    }
    const formData = new FormData();
    formData.append(RATING_FIELD, feedbackRating);
    if (feedbackText.trim()) formData.append(COMMENT_FIELD, feedbackText.trim());
    if (userEmail.trim()) formData.append(EMAIL_FIELD, userEmail.trim());

    fetch(GOOGLE_FORM_ACTION_URL, {
      method: 'POST',
      mode: 'no-cors',
      body: formData,
    });

    setFeedbackSubmitted(true);
    setTimeout(() => {
      setShowFeedbackModal(false);
      setFeedbackSubmitted(false);
      setFeedbackRating(0);
      setFeedbackText('');
      setUserEmail('');
    }, 2000);
  };

  const paypalDonateUrl = "https://www.paypal.com/donate/?hosted_button_id=FGPNV7YTRPPVE";
  const handleDonateFloater = () => {
    if (window.gtag) {
      window.gtag('event', 'donate_click', {
        event_category: 'engagement',
        event_label: 'Donate Floater Button'
      });
    }
    window.open(paypalDonateUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 font-sans">
      <Helmet>
        <title>User Guide | Truth AI Tech</title>
        <meta name="description" content="Learn how to use our free sports and trip planning tools. Tips, FAQs, and best practices for match scheduling and trip planning." />
        <link rel="canonical" href="https://truth-ai-tech.com/user-guide" />
      </Helmet>
      <div className="max-w-4xl mx-auto py-10 px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-blue-800 mb-4">Match Scheduler User Guide</h1>
          <p className="text-lg text-gray-600">Everything you need to know about creating perfect sports tournament schedules</p>
        </div>

        {/* Navigation */}
        <nav className="bg-white rounded-xl shadow-lg p-4 mb-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  activeSection === section.id
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {section.title}
              </button>
            ))}
          </div>
        </nav>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Getting Started */}
          {activeSection === "getting-started" && (
            <div>
              <h2 className="text-2xl font-bold text-blue-800 mb-6">Getting Started</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">1. Add Your Teams</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                    <li>Click the "+ Add Team" button to add more teams</li>
                    <li>Enter a unique team name (e.g., "Royals", "Vikings")</li>
                    <li>Assign each team to a group (e.g., "A", "B", "C")</li>
                    <li>Select preferred playing days for each team</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">2. Configure Tournament Settings</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                    <li>Set your tournament start date</li>
                    <li>Choose the number of grounds available</li>
                    <li>Configure matches per day for each day of the week</li>
                    <li>Set time slots for each day (e.g., "6:00 PM, 8:00 PM")</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">3. Generate Your Schedule</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                    <li>Choose whether to include umpiring assignments</li>
                    <li>Click "Generate Schedule" and wait for processing</li>
                    <li>Review your generated schedule and summaries</li>
                    <li>Copy the schedule to Excel if needed</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">Plan Your Next Trip</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                    <li>Want to plan a vacation or group trip? Try our <a href="/trip-planner" style={{color: '#007bff', textDecoration: 'underline', fontWeight: 500}}>AI Trip Planner</a> for instant, personalized itineraries and packing lists!</li>
                  </ul>
                </div>
                
              </div>
            </div>
          )}

          {/* Features */}
          {activeSection === "features" && (
            <div>
              <h2 className="text-2xl font-bold text-blue-800 mb-6">Features</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-blue-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-blue-800 mb-3">🎯 Round-Robin Scheduling</h3>
                  <p className="text-gray-600">Each team plays every other team in their group exactly once, ensuring fair competition.</p>
                </div>

                <div className="bg-green-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-green-800 mb-3">⚖️ Balanced Home/Away</h3>
                  <p className="text-gray-600">Uses mathematical algorithms to ensure teams get balanced home and away games.</p>
                </div>

                <div className="bg-purple-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-purple-800 mb-3">👨‍⚖️ Umpiring Assignments</h3>
                  <p className="text-gray-600">Automatically assigns umpiring teams with even distribution across all teams.</p>
                </div>

                <div className="bg-orange-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-orange-800 mb-3">📅 Flexible Scheduling</h3>
                  <p className="text-gray-600">Set different match counts and time slots for each day of the week.</p>
                </div>

                <div className="bg-red-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-red-800 mb-3">📊 Detailed Summaries</h3>
                  <p className="text-gray-600">Get insights on max games per week, home team distribution, and idle gaps.</p>
                </div>

                <div className="bg-indigo-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-indigo-800 mb-3">📋 Excel Export</h3>
                  <p className="text-gray-600">One-click copy to clipboard for easy import into Excel or other spreadsheet software.</p>
                </div>
              </div>
            </div>
          )}

          {/* Troubleshooting */}
          {activeSection === "troubleshooting" && (
            <div>
              <h2 className="text-2xl font-bold text-blue-800 mb-6">Troubleshooting</h2>
              
              <div className="space-y-6">
                <div className="border-l-4 border-red-500 pl-4">
                  <h3 className="text-lg font-semibold text-red-800 mb-2">❌ "Failed to generate schedule"</h3>
                  <p className="text-gray-600 mb-2">This usually means the constraints are too restrictive. Try:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4">
                    <li>Increasing the number of grounds</li>
                    <li>Adding more time slots per day</li>
                    <li>Extending the tournament duration</li>
                    <li>Reducing the number of teams</li>
                  </ul>
                </div>

                <div className="border-l-4 border-yellow-500 pl-4">
                  <h3 className="text-lg font-semibold text-yellow-800 mb-2">⚠️ "No schedule could be generated"</h3>
                  <p className="text-gray-600 mb-2">The algorithm couldn't find a valid schedule. Check:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4">
                    <li>All teams have at least one preferred day selected</li>
                    <li>You have enough time slots for the number of matches</li>
                    <li>Start date is not in the past</li>
                    <li>All team names and groups are filled in</li>
                  </ul>
                </div>

                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="text-lg font-semibold text-blue-800 mb-2">ℹ️ Schedule seems unbalanced</h3>
                  <p className="text-gray-600 mb-2">If home/away distribution looks uneven:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4">
                    <li>This is normal for odd numbers of teams</li>
                    <li>The algorithm optimizes for overall fairness</li>
                    <li>Check the "Home Team Summary" for detailed breakdown</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Tips & Best Practices */}
          {activeSection === "tips" && (
            <div>
              <h2 className="text-2xl font-bold text-blue-800 mb-6">Tips & Best Practices</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">🏆 Tournament Planning</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                    <li>Start with a realistic number of teams (4-12 per group works best)</li>
                    <li>Plan for at least 2-3 weeks for a complete round-robin</li>
                    <li>Consider weather and venue availability when setting time slots</li>
                    <li>Leave some buffer time between matches for setup/cleanup</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">⚙️ Optimal Settings</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                    <li>Use 2-4 grounds for most tournaments</li>
                    <li>Set weekend days (Sat/Sun) to have more matches and time slots</li>
                    <li>Weekday matches work well in the evening (6:00 PM, 8:00 PM)</li>
                    <li>Weekend matches can be spread throughout the day</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">📱 Mobile Usage</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                    <li>The app works great on mobile devices but works best on laptop/desktop.</li>
                    <li>Use landscape mode for better table viewing</li>
                    <li>Copy schedule to Excel for easier sharing</li>
                    <li>Bookmark the page for quick access</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* FAQ */}
          {activeSection === "faq" && (
            <div>
              <h2 className="text-2xl font-bold text-blue-800 mb-6">Frequently Asked Questions</h2>
              
              <div className="space-y-6">
                <div className="border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Q: How many teams can I add?</h3>
                  <p className="text-gray-600">A: There's no strict limit, but 4-12 teams per group works best. For larger tournaments, consider splitting into multiple groups.</p>
                </div>

                <div className="border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Q: Can I edit the schedule after generation?</h3>
                  <p className="text-gray-600">A: Currently, you need to regenerate the schedule with new parameters. Copy the schedule to Excel for manual edits.</p>
                </div>

                <div className="border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Q: How does umpiring assignment work?</h3>
                  <p className="text-gray-600">A: Teams from the same group umpire each other's matches, ensuring even distribution. Teams never umpire their own matches.</p>
                </div>

                <div className="border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Q: What if a team can't play on certain days?</h3>
                  <p className="text-gray-600">A: Use the "Preferences" checkboxes for each team to specify which days they can play. The scheduler will respect these preferences.</p>
                </div>

                <div className="border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Q: Is the schedule fair for all teams?</h3>
                  <p className="text-gray-600">A: Yes! The algorithm ensures each team plays every other team once, with balanced home/away games and fair ground distribution.</p>
                </div>

                <div className="border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Q: Can I use this for different sports?</h3>
                  <p className="text-gray-600">A: Absolutely! This works for any sport that uses round-robin tournament format - cricket, football, basketball, tennis, etc.</p>
                </div>

                <div className="border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Q: How do I export the schedule?</h3>
                  <p className="text-gray-600">A: Click "Copy Schedule Table for Excel" button, then paste into Excel or Google Sheets. The data will be properly formatted with tabs.</p>
                </div>

                <div className="border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Q: Is this tool free to use?</h3>
                  <p className="text-gray-600">A: Yes, completely free! If you find it helpful, consider supporting us with a coffee donation.</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-500">
          <p>Need more help? Use the feedback button to contact us!</p>
        </div>
      </div>

      {/* Floating Action Buttons Toggle */}
      <button
        onClick={() => setShowFloatingButtons(!showFloatingButtons)}
        className="fixed bottom-6 right-6 z-50 bg-gray-600 text-white rounded-full shadow-lg p-3 hover:bg-gray-700 transition-all duration-300 flex items-center justify-center"
        aria-label={showFloatingButtons ? "Hide floating buttons" : "Show floating buttons"}
      >
        {showFloatingButtons ? (
          // Collapse icon (chevron down)
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        ) : (
          // Expand icon (chevron up)
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        )}
      </button>

      {/* Floating Back to Scheduler Button */}
      {showFloatingButtons && (
        <a
          href="/match-scheduler"
          className="fixed bottom-52 right-6 z-40 bg-purple-600 text-white rounded-full shadow-lg px-6 py-3 font-semibold text-lg hover:bg-purple-700 transition-all duration-300 flex items-center gap-2 animate-fade-in"
          style={{position: 'fixed', bottom: 208, right: 24, zIndex: 9999}}
          aria-label="Back to Match Scheduler"
        >
          <svg className="w-4 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          <span className="hidden sm:inline">Scheduler</span>
        </a>
      )}
      {/* Donate Floater Button.. */}
      {showFloatingButtons && (
        <button
          className="fixed bottom-36 right-6 z-40 bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-full shadow-lg px-6 py-3 font-semibold text-lg hover:from-yellow-500 hover:to-orange-600 transition-all duration-300 flex items-center gap-1 animate-fade-in"
          style={{position: 'fixed', bottom: 144, right: 24, zIndex: 9999, justifyContent: 'center'}}
          onClick={handleDonateFloater}
          aria-label="Donate with PayPal"
        >
          <svg className="w-12 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41 0.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
          <span className="hidden sm:inline text-center">Donate</span>
        </button>
      )}
      {/* Floating Feedback Button */}
      {showFloatingButtons && (
        <button
          className="fixed bottom-20 right-6 z-40 bg-blue-600 text-white rounded-full shadow-lg px-6 py-3 font-semibold text-lg hover:bg-blue-700 transition-all duration-300 flex items-center gap-2 animate-fade-in"
          style={{position: 'fixed', bottom: 80, right: 24, zIndex: 9999}}
          onClick={() => setShowFeedbackModal(true)}
          aria-label="Give Feedback"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h4m-2 8a9 9 0 110-18 9 9 0 010 18z" /></svg>
          <span className="hidden sm:inline">Feedback</span>
        </button>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md relative animate-fade-in">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-2xl font-bold"
              onClick={() => setShowFeedbackModal(false)}
              aria-label="Close Feedback"
            >
              ×
            </button>
            {feedbackSubmitted ? (
              <div className="text-center py-8">
                <div className="text-3xl mb-2">🎉</div>
                <div className="text-lg font-semibold text-blue-700">Thank you for your feedback!</div>
              </div>
            ) : (
              <form onSubmit={handleFeedbackSubmit} className="flex flex-col gap-4">
                <h2 className="text-xl font-bold text-blue-800 mb-2 text-center">We value your feedback</h2>
                <div className="flex items-center justify-center gap-2">
                  {[1,2,3,4,5].map(star => (
                    <button
                      type="button"
                      key={star}
                      onClick={() => setFeedbackRating(star)}
                      className={star <= feedbackRating ? "text-yellow-400" : "text-gray-300"}
                      aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                    >
                      <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.175c.969 0 1.371 1.24.588 1.81l-3.38 2.455a1 1 0 00-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.54 1.118l-3.38-2.454a1 1 0 00-1.175 0l-3.38 2.454c-.784.57-1.838-.196-1.54-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.05 9.394c-.783-.57-.38-1.81.588-1.81h4.175a1 1 0 00.95-.69l1.286-3.967z" /></svg>
                    </button>
                  ))}
                </div>
                <textarea
                  className="border border-blue-200 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-300 transition min-h-[80px]"
                  placeholder="Your comments (optional)"
                  value={feedbackText}
                  onChange={e => setFeedbackText(e.target.value)}
                  maxLength={500}
                />
                <input
                  type="email"
                  className="border border-blue-200 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
                  placeholder="Your email (optional)"
                  value={userEmail}
                  onChange={e => setUserEmail(e.target.value)}
                  maxLength={100}
                />
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition mt-2"
                  disabled={feedbackRating === 0}
                >
                  Submit Feedback
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserGuide; 